import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { CardModule } from 'primeng/card';
import { ChipModule } from 'primeng/chip';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { PanelModule } from 'primeng/panel';
import { ProgressBarModule } from 'primeng/progressbar';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TooltipModule } from 'primeng/tooltip';
import { ServiceCard } from 'src/app/_models/ServiceCard.model';
import { DashboardConfigService } from 'src/app/dashboard/_services/dashboard-config.service';
import { SaveServiceCardAsWidgetRequest } from 'src/app/_models/dashboard/dashboard-config.model';

@Component({
	selector: 'app-service-cards',
	templateUrl: './service-cards.component.html',
	styleUrls: ['./service-cards.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, FormsModule, CardModule, ButtonModule, InputTextModule, AvatarModule, DividerModule, TagModule, ChipModule, ToggleButtonModule, DatePickerModule, SelectModule, ProgressBarModule, BadgeModule, TableModule, PanelModule, TooltipModule],
})
export class ServiceCardsComponent implements OnInit {
	@Input() card: ServiceCard = {
		srv_id: 1,
		srv_pt_rl_id: 1,
		srv_name: 'Preview',
		srv_name_view: 'Preview',
		srv_link: '',
		srv_icon: '',
		srv_background_class: 'bg-gradient-sunset',
		srv_background_opacity: 0.5,
	};

	backgroundStyles = [
		{ name: 'Gradient Sunset', class: 'bg-gradient-sunset' },
		{ name: 'Ocean Waves', class: 'bg-gradient-ocean' },
		{ name: 'Forest Green', class: 'bg-gradient-forest' },
		{ name: 'Cosmic Purple', class: 'bg-gradient-cosmic' },
		{ name: 'Mint Fresh', class: 'bg-gradient-mint' },
		{ name: 'Fire Glow', class: 'bg-gradient-fire' },
		{ name: 'Mesh Purple', class: 'bg-mesh-purple' },
		{ name: 'Geometric', class: 'bg-geometric' },
		{ name: 'Glass Morphism', class: 'bg-glass-morphism' },
		{ name: 'Neon Glow', class: 'bg-neon' },
		{ name: 'Dark Elegant', class: 'bg-dark-elegant' },
		{ name: 'Aurora Animation', class: 'bg-aurora' },
	];

	// Le card devono usare un colore piatto, non il gradient: ogni classe gradient
	// viene mappata alla corrispondente classe piatta (bg-solid-*) definita in background.css.
	private static readonly GRADIENT_TO_SOLID: Record<string, string> = {
		'bg-gradient-sunset': 'bg-solid-red',
		'bg-gradient-ocean': 'bg-solid-indigo',
		'bg-gradient-forest': 'bg-solid-forest',
		'bg-gradient-cosmic': 'bg-solid-violet',
		'bg-gradient-mint': 'bg-solid-teal',
		'bg-gradient-fire': 'bg-solid-rose',
		'bg-mesh-purple': 'bg-solid-indigo',
		'bg-geometric': 'bg-solid-red',
		'bg-glass-morphism': 'bg-solid-slate',
		'bg-neon': 'bg-solid-cyan',
		'bg-dark-elegant': 'bg-solid-slate',
		'bg-aurora': 'bg-solid-cyan',
		'bg-gradient-nebula': 'bg-solid-navy',
		'bg-gradient-midnight': 'bg-solid-midnight',
		'bg-gradient-galaxy': 'bg-solid-purple',
		'bg-gradient-aurora-borealis': 'bg-solid-sky',
		'bg-gradient-tropical': 'bg-solid-pink',
		'bg-gradient-berry': 'bg-solid-blue',
		'bg-gradient-citrus': 'bg-solid-orange',
		'bg-gradient-lavender': 'bg-solid-purple',
		'bg-gradient-autumn': 'bg-solid-red',
		'bg-gradient-mountain': 'bg-solid-slate',
		'bg-gradient-reef': 'bg-solid-teal',
		'bg-gradient-deep-sea': 'bg-solid-indigo',
		'bg-gradient-volcanic': 'bg-solid-purple',
		'bg-gradient-cyber': 'bg-solid-fuchsia',
		'bg-gradient-vapor': 'bg-solid-blue',
		'bg-gradient-synthwave': 'bg-solid-pink',
		'bg-gradient-electric': 'bg-solid-violet',
		'bg-gradient-plasma': 'bg-solid-rose',
		'bg-gradient-pulse': 'bg-solid-indigo',
		'bg-gradient-wave': 'bg-solid-orange',
		'bg-gradient-shift': 'bg-solid-indigo',
		'bg-mesh-gold': 'bg-solid-amber',
		'bg-mesh-ruby': 'bg-solid-red',
		'bg-gradient-bronze': 'bg-solid-brown',
		'bg-gradient-steel': 'bg-solid-zinc',
		'bg-gradient-platinum': 'bg-solid-slate',
		'bg-gradient-dusk': 'bg-solid-slate',
		'bg-gradient-twilight': 'bg-solid-indigo',
		'bg-gradient-mystic': 'bg-solid-gray',
		'bg-gradient-inferno': 'bg-solid-crimson',
		'bg-gradient-primary': 'bg-solid-blue',
	};

	/** Classe di sfondo della card forzata a colore piatto (nessun gradient). */
	get cardBackgroundClass(): string {
		const cls = this.card?.srv_background_class ?? '';
		return ServiceCardsComponent.GRADIENT_TO_SOLID[cls] ?? cls;
	}

	constructor(
		private router: Router,
		private dashboardConfigService: DashboardConfigService,
	) {}

	ngOnInit() {}

	/**
	 * Salva la service card come widget nella dashboard
	 */
	saveToDashboard(event: Event): void {
		event.stopPropagation(); // Previene il click sulla card

		const request: SaveServiceCardAsWidgetRequest = {
			title: this.card.srv_name_view || this.card.srv_name,
			icon: this.card.srv_icon,
			servicecardConfig: {
				srv_id: this.card.srv_id,
				srv_name: this.card.srv_name,
				srv_name_view: this.card.srv_name_view,
				srv_description: this.card.srv_description,
				srv_link: this.card.srv_link,
				srv_link_param: this.card.srv_link_param as Record<string, any>,
				srv_icon: this.card.srv_icon,
				srv_background_class: this.card.srv_background_class,
				srv_background_image: this.card.srv_background_image,
				srv_background_opacity: this.card.srv_background_opacity,
				srv_show_icon: this.card.srv_show_icon,
				srv_show_cmp_logo: this.card.srv_show_cmp_logo,
				srv_show_company_name: this.card.srv_show_company_name,
				srv_show_service_name: this.card.srv_show_service_name,
				srv_show_badge: this.card.srv_show_badge,
				srv_badge: this.card.srv_badge,
				company: this.card.company
					? {
							cmp_id: this.card.company.id,
							cmp_name: this.card.company.name || '',
							cmp_logo: this.card.company.cmp_logo_b64,
						}
					: undefined,
			},
		};

		this.dashboardConfigService.saveServiceCardAsWidget(request).subscribe();
	}

	async onCardClick(card: ServiceCard) {
		if (card.srv_link) this.router.navigate([card.srv_link], card.srv_link_param);
	}

	getImageOverlayStyles() {
		if (!this.card.srv_background_image) {
			return null;
		}

		const opacity = this.card.srv_background_opacity ?? 0.5;
		return {
			'background-image': `url('${this.card.srv_background_image}')`,
			'background-size': 'cover',
			'background-position': 'center',
			opacity: opacity,
		};
	}
}
/*
{
			id: '1',
			icon: 'brush-outline',
			logo: 'AD',
			companyName: 'Adobe Creative',
			serviceName: 'Design Services',
			description: 'Professional design and creative solutions',
			backgroundClass: 'bg-gradient-sunset',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		{
			id: '2',
			icon: 'analytics-outline',
			logo: 'AN',
			companyName: 'Analytics Pro',
			serviceName: 'Data Analysis',
			description: 'Advanced data analysis and reporting',
			backgroundClass: 'bg-gradient-ocean',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		{
			id: '3',
			icon: 'leaf-outline',
			logo: 'EC',
			companyName: 'EcoTracker',
			serviceName: 'Sustainability',
			description: 'Environmental monitoring solutions',
			backgroundClass: 'bg-gradient-forest',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		{
			id: '4',
			icon: 'rocket-outline',
			logo: 'SP',
			companyName: 'SpaceTech',
			serviceName: 'Space Solutions',
			description: 'Advanced space technology',
			backgroundClass: 'bg-mesh-purple',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		{
			id: '5',
			icon: 'target-outline',
			logo: 'TG',
			companyName: 'Target Marketing',
			serviceName: 'Digital Marketing',
			description: 'Precision marketing solutions',
			backgroundClass: 'bg-geometric',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		{
			id: '6',
			icon: 'diamond-outline',
			logo: 'PR',
			companyName: 'Premium Services',
			serviceName: 'VIP Experience',
			description: 'Luxury service offerings',
			backgroundClass: 'bg-glass-morphism',
			showIcon: true,
			showLogo: true,
			showCompanyName: true,
			showServiceName: true,
		},
		 */
